/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

/**
 * Locale prefix mode for URL routing
 * - "always": All locales have prefix (e.g., /en-US/page, /de/page) - DEFAULT
 * - "as-needed": Default locale has no prefix (e.g., /page), others do (e.g., /de/page)
 * - "never": No locale prefix in URLs (use with domain-based routing)
 */
export type LocalePrefixMode = "always" | "as-needed" | "never";

/**
 * Configuration for locale handling in a website
 *
 * @typeParam T - Union type of supported locale codes. Defaults to `string` for flexibility.
 *
 * @example
 * // Basic usage (flexible)
 * const config: LocaleConfig = {
 *   locales: ["en-US", "de", "fr"],
 *   defaultLocale: "en-US",
 *   localePrefix: "as-needed",
 * };
 *
 * @example
 * // Type-safe usage (compile-time validation)
 * type SupportedLocale = "en-US" | "de" | "fr";
 * const config: LocaleConfig<SupportedLocale> = {
 *   locales: ["en-US", "de", "fr"],
 *   defaultLocale: "en-US", // TypeScript ensures this is one of the locales
 *   localePrefix: "as-needed",
 * };
 */
export interface LocaleConfig<T extends string = string> {
	/** Array of supported locale codes (e.g., ["en-US", "de", "fr"]) */
	locales: readonly T[] | T[];
	/** Default locale to use as fallback. Must be one of the supported locales. */
	defaultLocale: NoInfer<T>;
	/** How locale prefixes appear in URLs. Defaults to "always" */
	localePrefix?: LocalePrefixMode;
}

export const LocaleCookieName = "EPIC_LOCALE";
